import { normalizeShading, SHADING_MODES, DEFAULT_AUTO_ANGLE_DEG } from './mesh-topology.js';

const AXES = ['x', 'y', 'z'];
const TRANSFORMS = {
  position: { key: 'position', command: 'moveObject', argument: 'position', verb: 'Move' },
  rotation: { key: 'rotationDeg', command: 'rotateObject', argument: 'rotation', verb: 'Rotate' },
  scale: { key: 'scale', command: 'scaleObject', argument: 'scale', verb: 'Scale' },
};

function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function displayNumber(value) {
  return Number.isFinite(value) ? String(value) : '';
}

function rollbackValue(value) {
  return typeof value === 'number' ? displayNumber(value) : String(value ?? '');
}

function selectedObjects(selection, registry) {
  return selection.ids.map((id) => registry.get(id)).filter(Boolean).map((object) => registry.describe(object));
}

export function createInspector({ root, selection, registry, bridge, isReady = () => true }) {
  const body = root.querySelector('#inspector-body') ?? element('div', 'inspector-body');
  body.id = 'inspector-body';
  if (!body.parentNode) root.append(body);
  let linkedScale = true;
  let panelError = '';
  let destroyed = false;

  function emitError(action, error) {
    panelError = error?.message ?? String(error);
    root.dispatchEvent(new CustomEvent('manualerror', {
      bubbles: true,
      detail: { action, error: panelError },
    }));
  }

  function addField(section, labelText, field, value, { disabled = false, type = 'number', mixed = false } = {}) {
    const label = element('label', 'inspector-field');
    const caption = element('span', 'field-label', labelText);
    const input = document.createElement('input');
    input.type = type;
    input.dataset.field = field;
    input.disabled = disabled || !isReady();
    input.setAttribute('aria-disabled', String(input.disabled));
    input.setAttribute('aria-invalid', 'false');
    if (mixed) input.placeholder = 'Mixed';
    else input.value = String(value ?? '');
    if (type === 'number') input.step = 'any';
    label.append(caption, input);
    section.append(label);
    return input;
  }

  function addSelectField(section, labelText, field, options, value, { disabled = false } = {}) {
    const label = element('label', 'inspector-field');
    const caption = element('span', 'field-label', labelText);
    const select = document.createElement('select');
    select.dataset.field = field;
    select.disabled = disabled || !isReady();
    select.setAttribute('aria-disabled', String(select.disabled));
    if (value === null) {
      const mixed = document.createElement('option');
      mixed.value = '';
      mixed.textContent = 'Mixed';
      select.append(mixed);
    }
    for (const option of options) {
      const item = document.createElement('option');
      item.value = option;
      item.textContent = option[0].toUpperCase() + option.slice(1);
      select.append(item);
    }
    select.value = value ?? '';
    label.append(caption, select);
    section.append(label);
    return select;
  }

  function addActions(section, objects) {
    const actions = element('div', 'inspector-actions');
    const definitions = [
      ['duplicate', 'Duplicate', objects.length === 0],
      ['mirror', 'Mirror', objects.length === 0],
      ['group', 'Group', objects.length < 2],
      ['ungroup', 'Ungroup', !objects.some((object) => object.type === 'group')],
      ['delete', 'Delete', objects.length === 0],
    ];
    for (const [name, text, disabled] of definitions) {
      const button = element('button', '', text);
      button.type = 'button';
      button.dataset.objectAction = name;
      button.disabled = disabled || !isReady();
      button.setAttribute('aria-disabled', String(button.disabled));
      actions.append(button);
    }
    section.append(actions);
  }

  function render() {
    if (destroyed) return;
    const objects = selectedObjects(selection, registry);
    body.replaceChildren();
    if (objects.length === 0) {
      body.append(element('div', 'panel-placeholder', 'Select an object to inspect it.'));
      return;
    }

    const objectSection = element('section', 'inspector-section');
    objectSection.append(element('h2', '', objects.length === 1 ? objects[0].name : 'Multiple objects'));
    if (objects.length === 1) {
      const identity = element('dl', 'inspector-identity');
      identity.innerHTML = '<dt>Name</dt><dd></dd><dt>ID</dt><dd></dd>';
      identity.children[1].textContent = objects[0].name;
      identity.children[3].textContent = objects[0].id;
      objectSection.append(identity);
    }

    for (const [name, config] of Object.entries(TRANSFORMS)) {
      const group = element('fieldset', 'inspector-vector');
      const legend = element('legend', '', name[0].toUpperCase() + name.slice(1));
      if (name === 'rotation') legend.append(' (degrees)');
      if (name === 'scale') {
        const link = element('label', 'scale-link');
        const toggle = document.createElement('input');
        toggle.type = 'checkbox';
        toggle.dataset.field = 'linked-scale';
        toggle.checked = linkedScale;
        toggle.disabled = !isReady();
        toggle.setAttribute('aria-disabled', String(toggle.disabled));
        link.append(toggle, document.createTextNode(' Linked'));
        legend.append(link);
      }
      group.append(legend);
      for (let index = 0; index < 3; index++) {
        addField(
          group,
          AXES[index].toUpperCase(),
          `${name}-${AXES[index]}`,
          objects.length === 1 ? displayNumber(objects[0][config.key][index]) : '',
          { disabled: objects.length !== 1, mixed: objects.length !== 1 },
        );
      }
      objectSection.append(group);
    }
    addActions(objectSection, objects);
    body.append(objectSection);

    const material = element('section', 'inspector-section');
    material.append(element('h2', '', 'Material'));
    const materialDisabled = objects.some((object) => object.type === 'group' || object.color == null);
    for (const [field, label, type] of [
      ['color', 'Color', 'color'],
      ['roughness', 'Roughness', 'number'],
      ['metalness', 'Metalness', 'number'],
    ]) {
      const values = objects.map((object) => object[field]);
      const shared = values.every((value) => value === values[0]);
      addField(material, label, field, shared ? values[0] : '', {
        disabled: materialDisabled,
        type: shared ? type : 'text',
        mixed: !shared,
      });
    }
    body.append(material);

    // Shading. Deliberately small — three modes and an angle that only matters
    // for one of them. Parity with the setShading command is the requirement;
    // a shading panel is not.
    const meshes = objects.filter((object) => object.type === 'mesh');
    if (meshes.length > 0) {
      const shadingSection = element('section', 'inspector-section');
      shadingSection.append(element('h2', '', 'Shading'));
      const shadingDisabled = meshes.length !== objects.length;

      const modes = meshes.map((object) => normalizeShading(object.params?.shading).mode);
      const sharedMode = modes.every((mode) => mode === modes[0]) ? modes[0] : null;
      addSelectField(shadingSection, 'Mode', 'shading-mode', SHADING_MODES, sharedMode, {
        disabled: shadingDisabled,
      });

      const angles = meshes.map((object) => normalizeShading(object.params?.shading).angleDeg);
      const sharedAngle = angles.every((angle) => angle === angles[0]) ? angles[0] : null;
      addField(shadingSection, 'Auto angle', 'shading-angle', sharedAngle ?? '', {
        // Only meaningful under auto; flat and smooth are fixed endpoints.
        disabled: shadingDisabled || sharedMode !== 'auto',
        mixed: sharedAngle === null,
      });
      body.append(shadingSection);
    }

    const error = element('div', 'field-error', panelError);
    error.setAttribute('role', 'alert');
    body.append(error);
  }

  function invalid(input, previous, message) {
    input.value = rollbackValue(previous);
    input.setAttribute('aria-invalid', 'true');
    panelError = message;
    const error = body.querySelector('.field-error');
    if (error) error.textContent = message;
  }

  async function commit(input) {
    const objects = selectedObjects(selection, registry);
    if (!isReady() || input.disabled || objects.length === 0) return;
    const field = input.dataset.field;
    if (!field || field === 'linked-scale') return;
    const active = objects.find((object) => object.id === selection.activeId) ?? objects.at(-1);

    let command;
    let argsFor;
    let label;
    let previous;
    if (field === 'shading-mode' || field === 'shading-angle') {
      const meshes = objects.filter((object) => object.type === 'mesh');
      if (meshes.length === 0) return;
      const current = normalizeShading(
        (meshes.find((object) => object.id === selection.activeId) ?? meshes.at(-1)).params?.shading,
      );

      let mode = current.mode;
      let angleDeg = current.angleDeg;
      if (field === 'shading-mode') {
        if (input.value === '') return;
        mode = input.value;
        // Carry the angle across so switching to auto and back does not lose it.
        if (mode === 'auto') angleDeg = current.mode === 'auto' ? current.angleDeg : DEFAULT_AUTO_ANGLE_DEG;
      } else {
        previous = current.angleDeg;
        const value = Number(input.value);
        if (input.value.trim() === '' || !Number.isFinite(value) || value < 0 || value > 180) {
          invalid(input, previous, 'Auto angle must be between 0 and 180 degrees.');
          return;
        }
        if (value === current.angleDeg) return;
        angleDeg = value;
      }

      command = 'setShading';
      argsFor = (id) => (mode === 'auto' ? { id, mode, angleDeg } : { id, mode });
      label = `Set shading for ${meshes.length === 1 ? (meshes[0].name ?? meshes[0].id) : `${meshes.length} objects`}`;
      input.setAttribute('aria-invalid', 'false');
      panelError = '';
      try {
        if (meshes.length === 1) {
          await bridge.execute(command, argsFor(meshes[0].id), { label });
        } else {
          await bridge.executeBatch(meshes.map((object) => [command, argsFor(object.id)]), { label });
        }
        render();
      } catch (error) {
        emitError(command, error);
        render();
      }
      return;
    }

    if (field in { color: true, roughness: true, metalness: true }) {
      previous = active[field];
      let value = input.value.trim();
      if (field === 'color') {
        if (!/^#[0-9a-f]{6}$/i.test(value)) {
          invalid(input, previous, 'Color must be a six-digit hex value.');
          return;
        }
        value = value.toLowerCase();
      } else {
        if (value === '' || !Number.isFinite(Number(value)) || Number(value) < 0 || Number(value) > 1) {
          invalid(input, previous, `${field[0].toUpperCase() + field.slice(1)} must be between 0 and 1.`);
          return;
        }
        value = Number(value);
      }
      if (objects.every((object) => object[field] === value)) return;
      command = 'setMaterial';
      argsFor = (id) => ({ id, [field]: value });
      label = `Set ${field} for ${objects.length === 1 ? active.name : `${objects.length} objects`}`;
    } else {
      const [kind, axis] = field.split('-');
      const config = TRANSFORMS[kind];
      const axisIndex = AXES.indexOf(axis);
      if (!config || axisIndex < 0 || objects.length !== 1) return;
      previous = active[config.key][axisIndex];
      if (input.value.trim() === '' || !Number.isFinite(Number(input.value))) {
        invalid(input, previous, `${kind[0].toUpperCase() + kind.slice(1)} must be a finite number.`);
        return;
      }
      const value = Number(input.value);
      if (value === previous) return;
      if (kind === 'scale' && linkedScale && previous === 0) {
        invalid(input, previous, 'Linked scale cannot derive a ratio from a zero axis.');
        return;
      }
      const vector = [...active[config.key]];
      if (kind === 'scale' && linkedScale && previous !== 0) {
        const ratio = value / previous;
        for (let index = 0; index < vector.length; index++) vector[index] *= ratio;
      } else {
        vector[axisIndex] = value;
      }
      command = config.command;
      argsFor = (id) => ({ id, [config.argument]: vector });
      label = `${config.verb} ${active.name}`;
    }

    input.setAttribute('aria-invalid', 'false');
    panelError = '';
    try {
      if (objects.length === 1) {
        await bridge.execute(command, argsFor(objects[0].id), { label });
      } else {
        await bridge.executeBatch(objects.map((object) => [command, argsFor(object.id)]), { label });
      }
      render();
    } catch (error) {
      emitError(command, error);
      render();
    }
  }

  function change(event) {
    if (event.target?.dataset.field === 'linked-scale') linkedScale = event.target.checked;
    // A select commits on change; there is no meaningful "still typing" state
    // for it the way there is for a text field.
    if (event.target?.dataset.field === 'shading-mode') void commit(event.target);
  }

  function focusout(event) {
    if (event.target?.matches?.('input[data-field]:not([type="checkbox"])')) void commit(event.target);
  }

  function keydown(event) {
    if (!event.target?.matches?.('input[data-field]')) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      event.target.blur();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      render();
    }
  }

  function click(event) {
    const button = event.target.closest?.('[data-object-action]');
    if (!button || button.disabled || !isReady()) return;
    root.dispatchEvent(new CustomEvent('objectaction', {
      bubbles: true,
      detail: { action: button.dataset.objectAction, source: 'inspector' },
    }));
  }

  body.addEventListener('change', change);
  body.addEventListener('focusout', focusout);
  body.addEventListener('keydown', keydown);
  body.addEventListener('click', click);
  render();

  return {
    render,
    destroy() {
      destroyed = true;
      body.removeEventListener('change', change);
      body.removeEventListener('focusout', focusout);
      body.removeEventListener('keydown', keydown);
      body.removeEventListener('click', click);
    },
  };
}
